import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  LinearProgress,
  Alert,
  Snackbar,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { X } from 'lucide-mui';
import 'iframe-resizer/js/iframeResizer.contentWindow';
import { config } from '../../../config';
import { getCustomLogger } from '../../lib/logger';
import AppStoreBadgeImage from './assets/appstore.svg';
import GooglePlayBadgeImage from './assets/googleplay.svg';

const logger = getCustomLogger('Microtools: common');

// Gemini model configuration
export const GEMINI_MODEL = 'gemini-2.0-flash-lite';

// Common interfaces
export interface UploadState {
  uploading: boolean;
  progress: number;
  uploadComplete: boolean;
  error: string | null;
}

export interface CopyState {
  copySuccess: boolean;
}

export interface MediaRecorderState extends UploadState {
  isRecording: boolean;
}

export interface MediaStreamRefs {
  mediaRecorder: React.MutableRefObject<MediaRecorder | null>;
  chunks: React.MutableRefObject<Blob[]>;
}

// Custom hooks
export const useIframeResizer = () => {
  useEffect(() => {
    // The iframe-resizer content window script auto-initializes
  }, []);
};

// Common utilities
export const finishProcessing = (url: string, openInNewTab?: boolean) => {
  // Get the part after the hash
  const hashPart = window.location.hash.split('?')[1] || '';
  const urlParams = new URLSearchParams(hashPart);
  const intent = urlParams.get('intent') || '';

  const redirectUrl = `https://screenapp.io/app/#/dashboard?importUrl=${encodeURIComponent(
    url
  )}${intent ? `&intent=${encodeURIComponent(intent)}` : ''}`;

  if (openInNewTab) {
    window.open(redirectUrl, '_blank');
  } else {
    window.parent.location.href = redirectUrl;
  }
};

// Common API functions
export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/-/g, '_') // Replace hyphens with underscores
    .replace(/#/g, '') // Remove hash symbols
    .replace(/[^a-zA-Z0-9._\s]/g, '') // Remove any other special characters
    .trim(); // Remove leading/trailing whitespace
};

export const uploadFileToS3 = async (
  blob: Blob,
  uploadUrl: string,
  downloadUrl: string,
  onProgress: (progress: number) => void,
  onComplete: () => void,
  openInNewTab?: boolean
) => {
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', uploadUrl, true);
  xhr.setRequestHeader('Content-Type', blob.type);

  xhr.upload.onprogress = (event) => {
    if (event.lengthComputable) {
      const progress = (event.loaded / event.total) * 100;
      onProgress(progress);
    }
  };

  xhr.onload = () => {
    if (xhr.status === 200) {
      onComplete();
      finishProcessing(downloadUrl, openInNewTab);
    }
  };

  xhr.send(blob);
};

export const uploadToScreenApp = async (
  blob: Blob,
  fileName: string,
  onStart: () => void,
  onProgress: (progress: number) => void,
  onComplete: () => void,
  onError: (error: string) => void,
  openInNewTab?: boolean
) => {
  onStart();

  try {
    const sanitizedFileName = sanitizeFileName(fileName);
    const response = await fetch(
      'https://api.screenapp.io/v2/temp-files/urls',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: sanitizedFileName }),
      }
    );

    const data = await response.json();
    if (data.success) {
      await uploadFileToS3(
        blob,
        data.data.uploadUrl,
        data.data.downloadUrl,
        onProgress,
        onComplete,
        openInNewTab
      );
    } else {
      throw new Error(data.message || 'Failed to get download URL');
    }
  } catch (error) {
    logger.error('Error uploading to screen app', error);
    onError('Upload failed. Please try again.');
  }
};

// Media handling utilities
export const initializeMediaRecorder = (
  mediaRecorder: React.MutableRefObject<MediaRecorder | null>,
  chunks: React.MutableRefObject<Blob[]>,
  onDataAvailable: (event: BlobEvent) => void
) => {
  const recorder = mediaRecorder.current;
  if (recorder) {
    recorder.ondataavailable = onDataAvailable;
    return Object.assign(chunks, { current: [] });
  }
  return chunks;
};

export const stopMediaRecording = (
  mediaRecorder: React.MutableRefObject<MediaRecorder | null>,
  streams: (MediaStream | null)[],
  animationFrame?: React.MutableRefObject<number | null>
) => {
  if (mediaRecorder.current) {
    mediaRecorder.current.stop();

    // Stop all tracks
    streams.forEach((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    });

    // Cancel animation frame if it exists
    if (animationFrame?.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  }
};

// Text to Speech API
export const convertTextToSpeech = async (text: string): Promise<Blob> => {
  const response = await fetch(
    'https://api.dev.screenapp.io/v2/proxy/groq/tts',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        input: text,
        model: 'playai-tts',
        voice: 'Arista-PlayAI',
        response_format: 'wav',
        speed: 1.0,
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to convert text to speech');
  }

  return response.blob();
};

// Common UI Components
interface ProgressModalProps {
  open: boolean;
  progress: number;
  isComplete: boolean;
  processingMessage?: string;
  completeMessage?: string;
  onReset?: () => void;
  onSaveAgain?: () => void;
  onClose?: () => void;
}

export const ProgressModal: React.FC<ProgressModalProps> = ({
  open,
  progress,
  isComplete,
  processingMessage = 'Please wait while we process your file',
  completeMessage = 'Your recording is now ready to be processed.',
  onReset,
  onSaveAgain,
  onClose,
}) => {
  const theme = useTheme();
  const { t: translate } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      hideBackdrop
      PaperProps={{
        sx: {
          p: 2,
          textAlign: 'center',
          borderRadius: '8px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        },
      }}
    >
      {onClose && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
            zIndex: 1,
          }}
        >
          <X sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      <DialogContent sx={{ pb: 1, px: 1 }}>
        <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem' }}>
          {isComplete ? completeMessage : processingMessage}
        </Typography>
        <Box sx={{ width: '100%', position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: theme.palette.success.main,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.75rem',
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Box>
      </DialogContent>
      {!completeMessage?.includes('redirect') && (
        <DialogActions
          sx={{ px: 1, pb: 1, pt: 0, justifyContent: 'center', gap: 1 }}
        >
          {isComplete && onSaveAgain && (
            <Button
              onClick={onSaveAgain}
              variant="contained"
              size="small"
              sx={{ px: 2, py: 0.5 }}
            >
              {translate('common.modal.saveAnother')}
            </Button>
          )}
          {isComplete && onReset && (
            <Button
              onClick={onReset}
              variant="outlined"
              size="small"
              sx={{ px: 2, py: 0.5 }}
            >
              {translate('common.modal.reset')}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

interface FeedbackSnackbarProps {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
  autoHideDuration?: number;
  onClose: () => void;
}

export const FeedbackSnackbar: React.FC<FeedbackSnackbarProps> = ({
  open,
  message,
  severity,
  autoHideDuration = 6000,
  onClose,
}) => {
  const theme = useTheme();

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        '& .MuiAlert-root': {
          bgcolor: theme.palette[severity].main,
          color: theme.palette[severity].contrastText,
        },
      }}
    >
      <Alert severity={severity} variant="filled" sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
};

// Processing Indicator Component
export const ProcessingIndicator: React.FC<{ size?: number }> = ({
  size = 16,
}) => {
  return (
    <CircularProgress
      size={size}
      sx={{
        color: 'primary.main',
        animationDuration: '1s',
      }}
    />
  );
};

export const callGeminiAPI = async (prompt: string): Promise<string> => {
  try {
    const response = await fetch(config.geminiProxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: GEMINI_MODEL,
        contents: [
          {
            parts: [{ text: prompt }],
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Gemini API response:', data);

    const result = data.candidates[0].content.parts[0].text;
    console.log('Generated result:', {
      resultLength: result.length,
      previewStart: `${result.substring(0, 100)}...`,
    });

    return result;
  } catch (error) {
    console.error('Error calling Gemini API:', error);
    throw error;
  }
};

interface AppStoreBadgeProps {
  mobileUrl: string;
  showLabel?: boolean;
  height?: number;
}

export const AppStoreBadge: React.FC<AppStoreBadgeProps> = ({
  mobileUrl,
  showLabel = true,
  height = 48,
}) => {
  const { t } = useTranslation();

  const badgeStyle = {
    display: 'block',
    height: `${height}px`,
    width: 'auto',
    padding: 0,
    margin: 0,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        height: showLabel ? 'auto' : `${height}px`,
      }}
    >
      {showLabel && (
        <Typography
          variant="caption"
          sx={{
            color: 'success.main',
            fontWeight: 500,
            fontSize: '0.7rem',
          }}
        >
          {t('tools.mobileappcta.nowAvailable')}
        </Typography>
      )}
      <Button
        component="a"
        href={mobileUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          p: 0,
          minWidth: 'auto',
          lineHeight: 0,
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'transparent',
          },
          borderRadius: 0,
          height: `${height}px`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        disableRipple
        disableFocusRipple
        disableTouchRipple
      >
        <img
          src={AppStoreBadgeImage}
          alt={t('tools.mobileappcta.appStoreAlt')}
          style={badgeStyle}
        />
      </Button>
    </Box>
  );
};

interface GooglePlayBadgeProps {
  mobileUrl: string;
  showLabel?: boolean;
  height?: number;
}

export const GooglePlayBadge: React.FC<GooglePlayBadgeProps> = ({
  mobileUrl,
  showLabel = true,
  height = 48,
}) => {
  const { t } = useTranslation();

  const badgeStyle = {
    display: 'block',
    height: `${height}px`,
    width: 'auto',
    padding: 0,
    margin: 0,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        height: showLabel ? 'auto' : `${height}px`,
      }}
    >
      {showLabel && (
        <Typography
          variant="caption"
          sx={{
            color: 'success.main',
            fontWeight: 500,
            fontSize: '0.7rem',
          }}
        >
          {t('tools.mobileappcta.nowAvailable')}
        </Typography>
      )}
      <Button
        component="a"
        href={mobileUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          p: 0,
          minWidth: 'auto',
          lineHeight: 0,
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'transparent',
          },
          borderRadius: 0,
          height: `${height}px`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        disableRipple
        disableFocusRipple
        disableTouchRipple
      >
        <img
          src={GooglePlayBadgeImage}
          alt={t('tools.mobileappcta.googlePlayAlt')}
          style={badgeStyle}
        />
      </Button>
    </Box>
  );
};
